/**
 * DRPX Courier — AppContext
 *
 * Zentraler State-Container. Statt 15+ Props durch App.jsx → Page zu fädeln,
 * können Pages `const { user, auftraege, openToast, ... } = useApp()` nutzen.
 *
 * Bereitgestellt von App.jsx via <AppProvider value={...}>
 * Konsumiert von Pages via useApp()
 *
 * @module contexts/AppContext
 */

import { createContext, useContext } from 'react';
import type { AppContext as AppContextType } from '../types.js';

const AppContext = createContext<AppContextType | null>(null);

interface AppProviderProps {
  value: AppContextType;
  children: React.ReactNode;
}

export function AppProvider({ value, children }: AppProviderProps): React.JSX.Element {
  return (
    <AppContext.Provider value={value}>
      {children}
    </AppContext.Provider>
  );
}

/**
 * useApp() — Zugriff auf den globalen App-State.
 * Wirft einen Error wenn außerhalb von <AppProvider> aufgerufen.
 */
export function useApp(): AppContextType {
  const ctx = useContext(AppContext);
  if (!ctx) {
    throw new Error('useApp() muss innerhalb von <AppProvider> verwendet werden');
  }
  return ctx;
}

export default AppContext;
